import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export interface DocItem {
  description?: string;
  quantity?: number;
  unit_price?: number;
  total_ht?: number;
}

export interface DocPayload {
  kind: "devis" | "facture";
  number: string;
  title?: string;
  contact_name?: string;
  items?: DocItem[];
  total_ht: number;
  tva_rate: number;
  total_ttc: number;
  status?: string;
  due_date?: string;
  created_at?: string;
  notes?: string;
  /** Company sender info */
  company?: {
    name?: string;
    address?: string;
    siret?: string;
    email?: string;
    phone?: string;
  };
}

const fmt = (n: number) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);

const dateFmt = (iso?: string) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR");
  } catch {
    return iso;
  }
};

/**
 * Génère et télécharge un PDF à la française (devis / facture) avec
 * en-tête entreprise, infos client, tableau des lignes, totaux HT/TVA/TTC
 * et mentions légales.
 */
export function generateDocumentPDF(doc: DocPayload) {
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const W = pdf.internal.pageSize.getWidth();
  const GOLD = [212, 168, 83] as const; // Grafton gold
  const DARK = [30, 30, 30] as const;
  const MUTED = [110, 110, 110] as const;

  const company = doc.company || {};
  const companyName = company.name || "Votre Entreprise";
  const isDevis = doc.kind === "devis";
  const title = (isDevis ? "DEVIS" : "FACTURE") + "  " + doc.number;

  // ── HEADER ─────────────────────────────────────────────
  pdf.setFillColor(...DARK);
  pdf.rect(0, 0, W, 36, "F");

  pdf.setTextColor(...GOLD);
  pdf.setFontSize(22);
  pdf.setFont("helvetica", "bold");
  pdf.text(companyName, 15, 18);

  pdf.setFontSize(9);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(230, 230, 230);
  const subtitleParts = [
    company.address,
    company.siret ? `SIRET : ${company.siret}` : null,
    company.email,
    company.phone,
  ].filter(Boolean);
  pdf.text(subtitleParts.join("  •  ") || "Propulsé par Grafton IA", 15, 25);

  pdf.setTextColor(...GOLD);
  pdf.setFontSize(14);
  pdf.setFont("helvetica", "bold");
  pdf.text(title, W - 15, 22, { align: "right" });

  // ── METADATA ───────────────────────────────────────────
  let y = 48;
  pdf.setTextColor(...MUTED);
  pdf.setFontSize(9);
  pdf.text("Émis le", 15, y);
  pdf.text(isDevis ? "Validité" : "Échéance", 65, y);
  pdf.text("Client", 115, y);

  pdf.setTextColor(...DARK);
  pdf.setFontSize(10);
  pdf.setFont("helvetica", "bold");
  pdf.text(dateFmt(doc.created_at), 15, y + 5);
  pdf.text(dateFmt(doc.due_date), 65, y + 5);
  pdf.text(doc.contact_name || "—", 115, y + 5, { maxWidth: 80 });

  if (doc.title) {
    y += 16;
    pdf.setTextColor(...MUTED);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.text("Objet", 15, y);
    pdf.setTextColor(...DARK);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.text(doc.title, 15, y + 5, { maxWidth: W - 30 });
    y += 5;
  }

  // ── LINE ITEMS TABLE ───────────────────────────────────
  const items =
    doc.items && doc.items.length > 0
      ? doc.items
      : [
          {
            description: doc.title || (isDevis ? "Prestation" : "Services"),
            quantity: 1,
            unit_price: doc.total_ht,
            total_ht: doc.total_ht,
          },
        ];

  autoTable(pdf, {
    startY: y + 12,
    head: [["Description", "Qté", "Prix unitaire HT", "Total HT"]],
    body: items.map((it) => [
      it.description || "—",
      (it.quantity ?? 1).toString(),
      fmt(it.unit_price ?? doc.total_ht),
      fmt(it.total_ht ?? (it.unit_price ?? 0) * (it.quantity ?? 1)),
    ]),
    headStyles: { fillColor: [...DARK], textColor: [...GOLD], fontStyle: "bold", fontSize: 9 },
    bodyStyles: { fontSize: 10, textColor: [...DARK] },
    alternateRowStyles: { fillColor: [248, 246, 240] },
    margin: { left: 15, right: 15 },
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { halign: "center", cellWidth: 20 },
      2: { halign: "right", cellWidth: 40 },
      3: { halign: "right", cellWidth: 35 },
    },
  });

  // ── TOTALS ─────────────────────────────────────────────
  const tvaAmount = (doc.total_ht * doc.tva_rate) / 100;
  const finalY = (pdf as any).lastAutoTable?.finalY || y + 30;

  const totalsX = W - 85;
  let ty = finalY + 10;

  pdf.setTextColor(...MUTED);
  pdf.setFontSize(9);
  pdf.setFont("helvetica", "normal");

  pdf.text("Total HT", totalsX, ty);
  pdf.setTextColor(...DARK);
  pdf.text(fmt(doc.total_ht), W - 15, ty, { align: "right" });

  ty += 6;
  pdf.setTextColor(...MUTED);
  pdf.text(`TVA (${doc.tva_rate}%)`, totalsX, ty);
  pdf.setTextColor(...DARK);
  pdf.text(fmt(tvaAmount), W - 15, ty, { align: "right" });

  ty += 2;
  pdf.setDrawColor(...GOLD);
  pdf.line(totalsX, ty + 2, W - 15, ty + 2);

  ty += 9;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.setTextColor(...DARK);
  pdf.text("TOTAL TTC", totalsX, ty);
  pdf.setTextColor(...GOLD);
  pdf.text(fmt(doc.total_ttc), W - 15, ty, { align: "right" });

  // ── NOTES ──────────────────────────────────────────────
  if (doc.notes) {
    ty += 15;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(...MUTED);
    pdf.text("Notes", 15, ty);
    pdf.setTextColor(...DARK);
    pdf.text(doc.notes, 15, ty + 5, { maxWidth: W - 30 });
  }

  // ── LEGAL FOOTER ───────────────────────────────────────
  const legal = isDevis
    ? "Devis valable 30 jours à compter de la date d'émission. En cas d'acceptation, veuillez dater, signer et retourner ce devis avec la mention « Bon pour accord »."
    : "Pénalités de retard : 3 fois le taux d'intérêt légal. Indemnité forfaitaire pour frais de recouvrement : 40 € (Art. L441-10 du Code de commerce). TVA acquittée sur les débits.";

  pdf.setFontSize(7);
  pdf.setTextColor(...MUTED);
  pdf.setFont("helvetica", "italic");
  pdf.text(legal, 15, 280, { maxWidth: W - 30 });

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.text("Document généré par Grafton IA — grafton.ai", W - 15, 290, { align: "right" });

  // ── DOWNLOAD ───────────────────────────────────────────
  const filename = `${isDevis ? "Devis" : "Facture"}_${doc.number}_${(doc.contact_name || "client").replace(/[^\w-]/g, "_")}.pdf`;
  pdf.save(filename);
}
